import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from 'context/AlertProvider';
import PageContainer from 'components/PageContainer/PageContainer';
import {
  Button,
  Card,
  Confirm,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import Portal from 'components/Portal/Portal';
import { runSequentially } from 'modules/run-sequentially/run-sequentially';
import { ITEMS_PER_PAGE } from 'constants/constants';
import TableWrapper from 'components/TableWrapper/TableWrapper';
import { useResetSelectedRows } from 'hooks/useResetSelectedRows';
import useFiltersReducer, {
  loadMoreAction,
  updateFiltersAction,
} from 'hooks/useFiltersReducer';
import MarketplacePendingOrdersTable from 'components/MarketplacePendingOrdersTable/MarketplacePendingOrdersTable';
import MarketplacePendingOrdersFiltersBar from 'components/MarketplacePendingOrdersFiltersBar/MarketplacePendingOrdersFiltersBar';
import { useLocalFilteredResults } from 'hooks/useLocalFilteredResults';
import DownloadCSVButton from 'components/DownloadCSVButton/DownloadCSVButton';
import './marketplace-pending-orders-page.scss';
import {
  dsvCancelOrder,
  dsvFailedOrders,
  dsvRetryOrder,
} from 'services/marketplaces.service';

function MarketplacePendingOrdersPage() {
  const alert = useAlert();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [confirmCancelIsVisible, setConfirmCancelIsVisible] = useState(false);
  const [confirmRetryIsVisible, setConfirmRetryIsVisible] = useState(false);
  const [selectedPendingOrders, setSelectedPendingOrders] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [query, setQuery] = useState('');
  const { lastResetSelectedRows, resetSelectedRows } = useResetSelectedRows();
  const hasError13Selected = useMemo(
    () => selectedPendingOrders.some((order) => order.status === '13'),
    [selectedPendingOrders],
  );

  const [state, dispatch] = useFiltersReducer({
    marketplace: '',
    page: 1,
  });

  const fetchPendingOrders = useCallback(
    async (state) => {
      try {
        setLoading(true);
        const { data: pendingOrders } = await dsvFailedOrders({
          marketplace: state.marketplace,
          page: state.page,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        setHasNextPage(pendingOrders?.length === ITEMS_PER_PAGE);
        setPendingOrders((prev) =>
          state.page > 1 ? [...prev, ...pendingOrders] : pendingOrders,
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [setHasNextPage, alert],
  );

  const resetView = useCallback(async () => {
    await fetchPendingOrders(state);
    resetSelectedRows();
  }, [state, fetchPendingOrders, resetSelectedRows]);

  const retryPendingOrders = useCallback(
    async (pendingOrders) => {
      try {
        setRetrying(true);
        setConfirmRetryIsVisible(false);
        await runSequentially(
          pendingOrders,
          async (order) => {
            try {
              await dsvRetryOrder(order.purchase_number);
            } catch (error) {
              throw error;
            }
          },
          {
            msBetweenEachRun: 500,
          },
        );
        setTimeout(async () => {
          alert.current.message(
            'Los pedidos se han reintentado con éxito.',
            async () => {
              await resetView();
              setRetrying(false);
            },
          );
        }, 4000);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
        await resetView();
        setRetrying(false);
      }
    },
    [resetView, alert],
  );

  const cancelPendingOrders = useCallback(
    async (pendingOrders) => {
      try {
        setCanceling(true);
        setConfirmCancelIsVisible(false);
        await runSequentially(
          pendingOrders,
          async (order) => {
            try {
              await dsvCancelOrder(order.purchase_number);
            } catch (error) {
              throw error;
            }
          },
          {
            msBetweenEachRun: 500,
          },
        );
        setTimeout(async () => {
          alert.current.message(
            'Los pedidos se han cancelado con éxito',
            async () => {
              await resetView();
              setCanceling(false);
            },
          );
        }, 4000);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
        await resetView();
        setCanceling(false);
      }
    },
    [resetView, alert],
  );

  const filteredPendingOrders = useLocalFilteredResults(pendingOrders, query);

  const updateFilters = useCallback(
    (filters) => {
      dispatch(updateFiltersAction(filters));
    },
    [dispatch],
  );

  useEffect(() => {
    (async () => {
      if (!!state.marketplace) await fetchPendingOrders(state);
    })();
  }, [state, fetchPendingOrders]);

  return (
    <PageContainer
      title="Pedidos pendientes"
      className="marketplace-pending-orders-page"
    >
      <Dimmer active={loading && state.page === 1} inverted>
        <Loader>Cargando pedidos…</Loader>
      </Dimmer>
      <Dimmer active={retrying} inverted>
        <Loader>Reintentando pedidos…</Loader>
      </Dimmer>
      <Dimmer active={canceling} inverted>
        <Loader>Anulando pedidos…</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <Portal.Out name="card-header-left" />
          <Portal.Out name="card-header" />
        </Card.Header>
        <Card.Content>
          <MarketplacePendingOrdersFiltersBar
            onFiltersChange={updateFilters}
            query={query}
            onChangeQuery={(query) => {
              setQuery(query);
              resetSelectedRows();
            }}
          />
          {!!filteredPendingOrders.length ? (
            <TableWrapper>
              <MarketplacePendingOrdersTable
                loading={loading}
                orders={filteredPendingOrders}
                hasNextPage={hasNextPage}
                lastResetSelectedRows={lastResetSelectedRows}
                onRowSelect={(selectedPendingOrders) =>
                  setSelectedPendingOrders(selectedPendingOrders)
                }
                onLoadMoreItems={async (skip) => {
                  const isFirstLoad = skip === 0;
                  if (isFirstLoad) return;
                  dispatch(loadMoreAction());
                }}
              />
              <footer className="marketplace-pending-orders-page__table-footer">
                <small>
                  {!!selectedPendingOrders?.length &&
                  selectedPendingOrders?.length === 1
                    ? '1 seleccionada'
                    : `${selectedPendingOrders?.length} seleccionadas`}
                </small>
                {!!filteredPendingOrders?.length &&
                  `Total: ${filteredPendingOrders?.length}`}
              </footer>
            </TableWrapper>
          ) : (
            <Message info>
              No se encuentra ningún pedido pendiente.
              {!state.marketplace && (
                <>
                  &nbsp;
                  <strong>
                    ¡Recuerda seleccionar un marketplace en el filtro superior!
                  </strong>
                </>
              )}
            </Message>
          )}
        </Card.Content>
        <Portal.In target="card-header-left">
          <Button
            basic
            icon="sync"
            content="Recargar"
            disabled={!state?.marketplace}
            onClick={(event, data) => resetView()}
          />
          <DownloadCSVButton pendingOrders={pendingOrders} />
        </Portal.In>
        <Portal.In target="card-header">
          <>
            <>
              <Button
                content="Anular"
                disabled={Boolean(!selectedPendingOrders?.length)}
                onClick={() => setConfirmCancelIsVisible(true)}
              />
              <Confirm
                open={confirmCancelIsVisible}
                content={`¿Confirmas la anulación de los pedidos seleccionados? Esta operación es irreversible`}
                cancelButton="Cancelar"
                confirmButton={`Anular ${selectedPendingOrders?.length || 0} ${
                  selectedPendingOrders?.length === 1 ? 'pedido' : 'pedidos'
                }`}
                onCancel={(event, data) => setConfirmCancelIsVisible(false)}
                onConfirm={() => cancelPendingOrders(selectedPendingOrders)}
              />
            </>
            <>
              <Button
                content="Reintentar"
                disabled={
                  Boolean(!selectedPendingOrders?.length) || hasError13Selected
                }
                primary
                onClick={() => setConfirmRetryIsVisible(true)}
              />
              <Confirm
                open={confirmRetryIsVisible}
                content="¿Confirmas el reintento de los pedidos seleccionadas?"
                cancelButton="Cancelar"
                confirmButton={`Reintentar ${
                  selectedPendingOrders?.length || 0
                } ${
                  selectedPendingOrders?.length === 1 ? 'pedido' : 'pedidos'
                }`}
                onCancel={(event, data) => setConfirmRetryIsVisible(false)}
                onConfirm={() => retryPendingOrders(selectedPendingOrders)}
              />
            </>
          </>
        </Portal.In>
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(MarketplacePendingOrdersPage);
